<template>
  <material-card
    scrollable
:title="$t('globals.coverage')"
    :color="$vuetify.theme.themes.light.secondary"
    icon="policy"
  >
    <v-dialog scrollable v-model="dialogs.edit" persistent max-width="1000">
      <material-card
        :title="$t(coverageFormTitle)"
        :icon="coverageFormIcon"
        :color="formColor"
      >
        <v-card-text v-if="item">
          <v-form ref="form" v-model="valid">
            <v-container fluid fill-height>
              <v-row wrap>
                <v-col cols="12" md="3">
                  <trans-enum
                    :value.sync="item.CoverageType"
                    :items="options.Enums.CoverageType"
                    :label="$t('headers.claim_submissions.coverage_type')"
                    :rules="[$rules.required]"
                    class="required"
                  ></trans-enum>
                </v-col>
                <v-col cols="12" md="3">
                  <trans-textbox
                    v-model="item"
                    :label="$t('globals.name')"
                    :rules="[$rules.required]"
                    class="required"
                    field="Name"
                    :dialog="dialogs.edit"
                  ></trans-textbox>
                </v-col>
                <v-col cols="12" md="3">
                  <no-autocomplete-textfield
                    style=""
                    :value="item.Order"
                    @change="item.Order = $event"
                    :label="$t('globals.order')"
                    :rules="[$rules.requiredNum]"
                    class="required"
                    type="number"
                    min="0"
                  ></no-autocomplete-textfield>
                </v-col>
                <v-col cols="12" md="3">
                  <auto-select
                    :value.sync="fundingMethod"
                    :label="$t('components.plancreationwizard.funding_method')"
                    :rules="[$rules.required]"
                    class="required"
                    :items="fundingMethods"
                    :slots="true"
                  >
                    <template v-slot:selection="data">
                      <slot name="selection" v-bind="data">
                        {{ $t(`enums.${data.item}`) }}
                      </slot>
                    </template>
                    <template v-slot:item="data">
                      <slot name="item" v-bind="data">
                        {{ $t(`enums.${data.item}`) }}
                      </slot>
                    </template>
                  </auto-select>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                  v-if="fundingMethod === 'Insurance Carrier'"
                >
                  <trans-select2
                    ref="insuranceCarrier"
                    v-model="item"
                    propertyName="PlanInsuranceCarrier"
                    :items="planInsuranceCarriers"
                    :label="$t('views.coverage.covered_by')"
                    :displayFunc="
                      (item, str) => `${str} (${item.Policy.Number})`
                    "
                    :rules="[$rules.required]"
                    class="required"
                    field="Name"
                    itemValue="Identifier"
                    transPropertyName="InsuranceCarrier"
                  ></trans-select2>
                </v-col>
                <v-col
                  cols="12"
                  md="3"
                  v-else-if="fundingMethod === 'Trust Fund'"
                >
                  <trans-select2
                    v-model="item"
                    propertyName="TrustFund"
                    :items="[trustFund]"
                    :label="$t('views.coverage.covered_by')"
                    :rules="[$rules.required]"
                    class="required"
                    field="Name"
                    itemValue="Id"
                  ></trans-select2>
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="item.Broker"
                    :items="brokers"
                    :label="$t('views.coverage.brokered_by')"
                    item-value="Id"
                    :item-text="(item) => `${item.FirstName} ${item.LastName}`"
                    clearable
                    return-object
                  ></v-select>
                </v-col>
              </v-row>
              <v-row wrap>
                <v-col cols="12">
                  <trans-textarea
                    v-model="item"
                    :label="$t('globals.description')"
                    :rules="[$rules.required]"
                    class="required"
                    field="Description"
                    :dialog="dialogs.edit"
                  ></trans-textarea>
                </v-col>
              </v-row>
              <v-row wrap>
                <v-col cols="12" md="3" v-if="hasBillingImport">
                  <v-checkbox
                    v-model="item.IncludedInEmployerContribution"
                    :label="
                      $t(
                        'components.plancreationwizard.included_in_employer_contribution'
                      )
                    "
                  ></v-checkbox>
                </v-col>
                <v-col xs="12" sm="6" md="3" lg="3">
                  <v-checkbox
                    v-model="item.IsFreeOfCharge"
                    :label="$t('components.plancreationwizard.free_of_charge')"
                  ></v-checkbox>
                </v-col>
                <v-col
                  xs="12"
                  sm="6"
                  md="3"
                  lg="3"
                  v-if="item.CoverageType === 'Health'"
                >
                  <v-checkbox
                    v-model="item.HasDrugs"
                    :label="$t('components.plancreationwizard.has_drugs')"
                  ></v-checkbox>
                </v-col>
                <template v-if="item.IsFreeOfCharge">
                  <v-col xs="12" sm="6" md="3" lg="3">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-container pa-0>
                          <v-row no-gutters>
                            <v-col cols="auto">
                              <v-checkbox
                                v-model="item.HasCoverageChoice"
                                :label="
                                  $t(
                                    'components.plancreationwizard.customizable_coverage'
                                  )
                                "
                              ></v-checkbox>
                            </v-col>
                            <v-col
                              style="margin-top: 20px; margin-left: 3px"
                              cols="auto"
                            >
                              <v-icon v-bind="attrs" v-on="on">help</v-icon>
                            </v-col>
                          </v-row>
                        </v-container>
                      </template>
                      <span>
                        {{
                          $t(
                            "components.plancreationwizard.customizable_coverage_tooltip"
                          )
                        }}
                      </span>
                    </v-tooltip>
                  </v-col>
                  <template v-if="item.HasCoverageChoice">
                    <v-col xs="12" sm="6" md="4" lg="4">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-container pa-0>
                            <v-row no-gutters>
                              <v-col cols="auto">
                                <trans-enum
                                  :value.sync="item.TracksDependantsEnum"
                                  :items="options.Enums.DependantTracking"
                                  :label="
                                    $t(
                                      'components.plancreationwizard.track_dependants'
                                    )
                                  "
                                ></trans-enum>
                              </v-col>
                              <v-col
                                style="margin-top: 20px; margin-left: 3px"
                                cols="auto"
                              >
                                <v-icon v-bind="attrs" v-on="on">help</v-icon>
                              </v-col>
                            </v-row>
                          </v-container>
                        </template>
                        <span>
                          {{
                            $t(
                              "components.plancreationwizard.track_dependants_tooltip"
                            )
                          }}
                        </span>
                      </v-tooltip>
                    </v-col>
                    <v-col xs="12" sm="6" md="4" lg="4">
                      <v-checkbox
                        v-model="item.RestrictToDefaultCoverageStatus"
                        :label="
                          $t(
                            'components.plancreationwizard.restrict_to_default_coverage_status'
                          )
                        "
                      ></v-checkbox>
                    </v-col>
                  </template>
                  <v-col xs="12" sm="6" md="3" lg="3">
                    <v-checkbox
                      v-model="item.HasCOB"
                      :label="$t('components.plancreationwizard.has_cob')"
                    ></v-checkbox>
                  </v-col>
                </template>
                <template v-if="!item.IsFreeOfCharge">
                  <v-col xs="12" sm="6" md="3" lg="3">
                    <v-checkbox
                      v-model="item.IsMandatory"
                      :label="$t('components.plancreationwizard.mandatory')"
                    ></v-checkbox>
                  </v-col>
                  <template v-if="!item.IsMandatory">
                    <v-col xs="12" sm="6" md="3" lg="3">
                      <v-checkbox
                        v-model="item.IsOptional"
                        :label="$t('components.plancreationwizard.optional')"
                      ></v-checkbox>
                    </v-col>
                    <v-col xs="12" sm="6" md="3" lg="3">
                      <v-checkbox
                        v-model="item.IsWaivable"
                        :label="$t('components.plancreationwizard.waivable')"
                      ></v-checkbox>
                    </v-col>
                  </template>
                  <v-col xs="12" sm="6" md="3" lg="3">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-container pa-0>
                          <v-row no-gutters>
                            <v-col cols="auto">
                              <v-checkbox
                                v-model="item.HasVolume"
                                :label="
                                  $t('components.plancreationwizard.volume')
                                "
                              ></v-checkbox>
                            </v-col>
                            <v-col
                              style="margin-top: 20px; margin-left: 3px"
                              cols="auto"
                            >
                              <v-icon v-bind="attrs" v-on="on">help</v-icon>
                            </v-col>
                          </v-row>
                        </v-container>
                      </template>
                      <span>
                        {{
                          $t(
                            "components.plancreationwizard.coverage_volume_tooltip"
                          )
                        }}
                      </span>
                    </v-tooltip>
                  </v-col>
                  <v-col xs="12" sm="6" md="3" lg="3" v-if="item.HasVolume">
                    <v-checkbox
                      v-model="item.HasNEL"
                      :label="$t('components.plancreationwizard.has_nel')"
                    ></v-checkbox>
                  </v-col>
                  <v-col
                    xs="12"
                    sm="6"
                    md="3"
                    lg="3"
                    v-if="item.HasVolume && item.HasNEL"
                  >
                    <currency-textfield
                      :value="item.NEL"
                      @change="item.NEL = $event"
                      :label="$t('components.plancreationwizard.nel')"
                      :rules="[$rules.requiredNum]"
                      class="required"
                    />
                    <!-- <no-autocomplete-textfield
                      :value="item.NEL"
                      @change="item.NEL = $event"
                      :label="$t('components.plancreationwizard.nel')"
                      prefix="$"
                      @keydown="currencyKeydown"
                      @keyup="currencyKeyUp"
                      @blur="nelBlur"
                      :rules="[$rules.decimal2, $rules.requiredNum]"
                      class="required"
                    ></no-autocomplete-textfield> -->
                  </v-col>

                  <v-col xs="12" sm="6" md="3" lg="3">
                    <v-checkbox
                      v-model="item.HasVolumeChoice"
                      :label="$t('components.plancreationwizard.volume_choice')"
                    ></v-checkbox>
                  </v-col>
                  <template v-if="item.HasVolumeChoice">
                    <v-col xs="12" sm="6" md="3" lg="3">
                      <no-autocomplete-textfield
                        type="number"
                        :label="$t('components.plancreationwizard.min_volume')"
                        :value="item.MinVolume"
                        @change="item.MinVolume = $event"
                        :rules="[rules.decimal, rules.minmax]"
                      />
                    </v-col>
                    <v-col xs="12" sm="6" md="3" lg="3">
                      <no-autocomplete-textfield
                        type="number"
                        :label="$t('components.plancreationwizard.max_volume')"
                        :value="item.MaxVolume"
                        @change="item.MaxVolume = $event"
                        :rules="[rules.decimal, rules.minmax]"
                      />
                    </v-col>
                  </template>
                  <v-col xs="12" sm="6" md="3" lg="3">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-container pa-0>
                          <v-row no-gutters>
                            <v-col cols="auto">
                              <v-checkbox
                                v-model="item.HasCoverageChoice"
                                :label="
                                  $t(
                                    'components.plancreationwizard.customizable_coverage'
                                  )
                                "
                              ></v-checkbox>
                            </v-col>
                            <v-col
                              style="margin-top: 20px; margin-left: 3px"
                              cols="auto"
                            >
                              <v-icon v-bind="attrs" v-on="on">help</v-icon>
                            </v-col>
                          </v-row>
                        </v-container>
                      </template>
                      <span>
                        {{
                          $t(
                            "components.plancreationwizard.customizable_coverage_tooltip"
                          )
                        }}
                      </span>
                    </v-tooltip>
                  </v-col>
                  <template v-if="item.HasCoverageChoice">
                    <v-col xs="12" sm="6" md="4" lg="4">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-container pa-0>
                            <v-row no-gutters>
                              <v-col cols="auto">
                                <!-- <auto-select
                                  :value.sync="item.TracksDependantsEnum"
                                  :items="options.Enums.DependantTracking"
                                  :label="
                                    $t(
                                      'components.plancreationwizard.track_dependants'
                                    )
                                  "
                                  :slots="true"
                                  :filter="filterFunc"
                                  clearable
                                >
                                  <template v-slot:selection="data">
                                    {{ $t("enums." + data.item) }}
                                  </template>
                                  <template v-slot:item="data">
                                    <v-list-item-content
                                      v-text="$t('enums.' + data.item)"
                                    ></v-list-item-content>
                                  </template>
                                </auto-select> -->
                                <trans-enum
                                  :value.sync="item.TracksDependantsEnum"
                                  :items="options.Enums.DependantTracking"
                                  :label="
                                    $t(
                                      'components.plancreationwizard.track_dependants'
                                    )
                                  "
                                ></trans-enum>
                              </v-col>
                              <v-col
                                style="margin-top: 20px; margin-left: 3px"
                                cols="auto"
                              >
                                <v-icon v-bind="attrs" v-on="on">help</v-icon>
                              </v-col>
                            </v-row>
                          </v-container>
                        </template>
                        <span>
                          {{
                            $t(
                              "components.plancreationwizard.track_dependants_tooltip"
                            )
                          }}
                        </span>
                      </v-tooltip>
                    </v-col>
                    <v-col xs="12" sm="6" md="4" lg="4">
                      <v-checkbox
                        v-model="item.RestrictToDefaultCoverageStatus"
                        :label="
                          $t(
                            'components.plancreationwizard.restrict_to_default_coverage_status'
                          )
                        "
                      ></v-checkbox>
                    </v-col>
                  </template>
                  <v-col xs="12" sm="6" md="3" lg="3">
                    <v-checkbox
                      v-model="item.HasCOB"
                      :label="$t('components.plancreationwizard.has_cob')"
                    ></v-checkbox>
                  </v-col>
                </template>
              </v-row>
              <template v-if="!item.IsFreeOfCharge">
                <v-row wrap >
                  <v-col cols="12" md="3">
                    <v-checkbox
                      v-model="item.IsTaxableBenefit"
                      :label="$t('components.plancreationwizard.taxable_benefit')"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-container pa-0>
                          <v-row no-gutters>
                            <v-col cols="auto">
                              <v-checkbox
                                v-model="includeTaxes"
                                :label="
                                  $t('components.plancreationwizard.taxable')
                                "
                              ></v-checkbox>
                            </v-col>
                            <v-col
                              style="margin-top: 20px; margin-left: 3px"
                              cols="auto"
                            >
                              <v-icon v-bind="attrs" v-on="on">help</v-icon>
                            </v-col>
                          </v-row>
                        </v-container>
                      </template>
                      <span>
                        {{
                          $t("components.plancreationwizard.coverage_tax_tooltip")
                        }}
                      </span>
                    </v-tooltip>
                  </v-col>
                </v-row>
                <v-row v-if="includeTaxes">
                  <v-card-text>
                    {{
                      $t(
                        "components.plancreationwizard.premiums_self_funded_remittance_taxes"
                      )
                    }}
                  </v-card-text>
                  <v-col cols="12" md="3">
                    <v-checkbox
                      v-model="item.IncludePPT"
                      :label="$t('components.plancreationwizard.include_ppt')"
                      :rules="[rules.atLeastOneTax]"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-checkbox
                      v-model="item.IncludeRST"
                      :label="$t('components.plancreationwizard.include_rst')"
                      :rules="[rules.atLeastOneTax]"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-checkbox
                      v-model="item.IncludeHST"
                      :label="$t('components.plancreationwizard.include_hst')"
                      :rules="[rules.atLeastOneTax]"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-checkbox
                      v-model="item.IncludeGST"
                      :label="$t('components.plancreationwizard.include_gst')"
                      :rules="[rules.atLeastOneTax]"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-checkbox
                      v-model="item.IncludePST"
                      :label="$t('components.plancreationwizard.include_pst')"
                      :rules="[rules.atLeastOneTax]"
                    ></v-checkbox>
                  </v-col>
                </v-row>

                <template v-if="hasAdminFees">
                  <v-row>
                    <v-col xs="12" sm="6" md="3" lg="3">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-container pa-0>
                            <v-row no-gutters>
                              <v-col cols="auto">
                                <v-checkbox
                                  v-model="item.HasAdminFees"
                                  :label="
                                    $t('components.plancreationwizard.admin_fees')
                                  "
                                ></v-checkbox>
                              </v-col>
                              <v-col
                                style="margin-top: 20px; margin-left: 3px"
                                cols="auto"
                              >
                                <v-icon v-bind="attrs" v-on="on">help</v-icon>
                              </v-col>
                            </v-row>
                          </v-container>
                        </template>
                        <span>
                          {{
                            $t(
                              "components.plancreationwizard.coverage_admin_fees_tooltip"
                            )
                          }}
                        </span>
                      </v-tooltip>
                    </v-col>
                    <v-col cols="12" md="4" v-if="item.HasAdminFees">
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-container pa-0>
                            <v-row no-gutters>
                              <v-col cols="auto">
                                <v-checkbox
                                  v-model="includeAdminTaxes"
                                  :label="
                                    $t(
                                      'components.plancreationwizard.include_admin_fee_taxes'
                                    )
                                  "
                                ></v-checkbox>
                              </v-col>
                              <v-col
                                style="margin-top: 20px; margin-left: 3px"
                                cols="auto"
                              >
                                <v-icon v-bind="attrs" v-on="on">help</v-icon>
                              </v-col>
                            </v-row>
                          </v-container>
                        </template>
                        <span>
                          {{
                            $t(
                              "components.plancreationwizard.coverage_admin_fee_tax_tooltip"
                            )
                          }}
                        </span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                  <v-row v-if="includeAdminTaxes">
                    <v-card-text>
                      {{ $t("components.plancreationwizard.admin_fee_taxes") }}
                    </v-card-text>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeAdminPPT"
                        :label="
                          $t('components.plancreationwizard.include_admin_ppt')
                        "
                        :rules="[rules.atLeastOneAdminTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeAdminRST"
                        :label="
                          $t('components.plancreationwizard.include_admin_rst')
                        "
                        :rules="[rules.atLeastOneAdminTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeAdminHST"
                        :label="
                          $t('components.plancreationwizard.include_admin_hst')
                        "
                        :rules="[rules.atLeastOneAdminTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeAdminGST"
                        :label="
                          $t('components.plancreationwizard.include_admin_gst')
                        "
                        :rules="[rules.atLeastOneAdminTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeAdminPST"
                        :label="
                          $t('components.plancreationwizard.include_admin_pst')
                        "
                        :rules="[rules.atLeastOneAdminTax]"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col xs="12" sm="6" md="3" lg="3">
                      <v-checkbox
                        v-model="item.IncludeClaimAdminFee"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_admin_fee'
                          )
                        "
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" v-if="item.IncludeClaimAdminFee">
                      <v-checkbox
                        v-model="includeClaimAdminTaxes"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_admin_fee_taxes'
                          )
                        "
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                  
                  <v-row v-if="includeClaimAdminTaxes">
                    <v-card-text>
                      {{
                        $t("components.plancreationwizard.claim_admin_fee_taxes")
                      }}
                    </v-card-text>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeClaimPPTAdminTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_admin_fee_ppt'
                          )
                        "
                        :rules="[rules.atLeastOneClaimAdminFeeTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeClaimRSTAdminTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_admin_fee_rst'
                          )
                        "
                        :rules="[rules.atLeastOneClaimAdminFeeTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeClaimHSTAdminTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_admin_fee_hst'
                          )
                        "
                        :rules="[rules.atLeastOneClaimAdminFeeTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeClaimPSTAdminTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_admin_fee_pst'
                          )
                        "
                        :rules="[rules.atLeastOneClaimAdminFeeTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeClaimGSTAdminTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_admin_fee_gst'
                          )
                        "
                        :rules="[rules.atLeastOneClaimAdminFeeTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeClaimQSTAdminTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_admin_fee_qst'
                          )
                        "
                        :rules="[rules.atLeastOneClaimAdminFeeTax]"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </template>
                <template v-if="
                      hasOperatingFees &&
                      !(hasOperatingFees && operatingFeesPerTerm)
                    ">
                  <v-row wrap>
                    <v-col
                      xs="12"
                      sm="6"
                      md="3"
                      lg="3"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-container pa-0>
                            <v-row no-gutters>
                              <v-col cols="auto">
                                <v-checkbox
                                  v-model="item.HasOperatingFees"
                                  :label="
                                    $t(
                                      'components.plancreationwizard.operating_fees'
                                    )
                                  "
                                ></v-checkbox>
                              </v-col>
                              <v-col
                                style="margin-top: 20px; margin-left: 3px"
                                cols="auto"
                              >
                                <v-icon v-bind="attrs" v-on="on">help</v-icon>
                              </v-col>
                            </v-row>
                          </v-container>
                        </template>
                        <span>
                          {{
                            $t(
                              "components.plancreationwizard.coverage_operating_fees_tooltip"
                            )
                          }}
                        </span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </template>
                <template v-if="hasOperatingFees && !operatingFeesPerTerm">
                  <v-row wrap>
                    <v-col
                      cols="12"
                      md="4"
                    >
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <v-container pa-0>
                            <v-row no-gutters>
                              <v-col cols="auto">
                                <v-checkbox
                                  v-model="includeOperatingTaxes"
                                  :label="
                                    $t(
                                      'components.plancreationwizard.include_operating_fee_taxes'
                                    )
                                  "
                                ></v-checkbox>
                              </v-col>
                              <v-col
                                style="margin-top: 20px; margin-left: 3px"
                                cols="auto"
                              >
                                <v-icon v-bind="attrs" v-on="on">help</v-icon>
                              </v-col>
                            </v-row>
                          </v-container>
                        </template>
                        <span>
                          {{
                            $t(
                              "components.plancreationwizard.coverage_operating_fee_tax_tooltip"
                            )
                          }}
                        </span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                  <v-row v-if="includeOperatingTaxes">
                    <v-card-text>
                      {{ $t("components.plancreationwizard.operating_fee_taxes") }}
                    </v-card-text>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeOperatingPPT"
                        :label="
                          $t('components.plancreationwizard.include_operating_ppt')
                        "
                        :rules="[rules.atLeastOneOperatingTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeOperatingRST"
                        :label="
                          $t('components.plancreationwizard.include_operating_rst')
                        "
                        :rules="[rules.atLeastOneOperatingTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeOperatingHST"
                        :label="
                          $t('components.plancreationwizard.include_operating_hst')
                        "
                        :rules="[rules.atLeastOneOperatingTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeOperatingGST"
                        :label="
                          $t('components.plancreationwizard.include_operating_gst')
                        "
                        :rules="[rules.atLeastOneOperatingTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeOperatingPST"
                        :label="
                          $t('components.plancreationwizard.include_operating_pst')
                        "
                        :rules="[rules.atLeastOneOperatingTax]"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </template>
                <template v-if="item.Broker">
                  <v-row>
                    <v-col xs="12" sm="6" md="3" lg="3">
                      <v-checkbox
                        v-model="item.HasCommissions"
                        :label="
                          $t(
                            'components.plancreationwizard.include_commissions'
                          )
                        "
                      ></v-checkbox>
                    </v-col>
                    <v-col xs="12" sm="6" md="3" lg="3" v-if="item.HasCommissions">
                      <v-checkbox
                        v-model="includeCommissionTaxes"
                        :label="
                          $t(
                            'components.plancreationwizard.include_commission_taxes'
                          )
                        "
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                  <v-row v-if="includeCommissionTaxes">
                    <v-card-text>
                      {{ $t("components.plancreationwizard.commission_taxes") }}
                    </v-card-text>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeCommissionPPT"
                        :label="
                          $t('components.plancreationwizard.include_commission_ppt')
                        "
                        :rules="[rules.atLeastOneCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeCommissionRST"
                        :label="
                          $t('components.plancreationwizard.include_commission_rst')
                        "
                        :rules="[rules.atLeastOneCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeCommissionHST"
                        :label="
                          $t('components.plancreationwizard.include_commission_hst')
                        "
                        :rules="[rules.atLeastOneCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeCommissionGST"
                        :label="
                          $t('components.plancreationwizard.include_commission_gst')
                        "
                        :rules="[rules.atLeastOneCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeCommissionPST"
                        :label="
                          $t('components.plancreationwizard.include_commission_pst')
                        "
                        :rules="[rules.atLeastOneCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col xs="12" sm="6" md="3" lg="3">
                      <v-checkbox
                        v-model="item.IncludeClaimCommission"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_commission'
                          )
                        "
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" v-if="item.IncludeClaimCommission">
                      <v-checkbox
                        v-model="includeClaimCommissionTaxes"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_commission_taxes'
                          )
                        "
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                  <v-row v-if="includeClaimCommissionTaxes">
                    <v-card-text>
                      {{
                        $t("components.plancreationwizard.claim_commission_taxes")
                      }}
                    </v-card-text>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeClaimCommissionPPTTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_commission_ppt'
                          )
                        "
                        :rules="[rules.atLeastOneClaimCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeClaimCommissionRSTTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_commission_rst'
                          )
                        "
                        :rules="[rules.atLeastOneClaimCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeClaimCommissionHSTTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_commission_hst'
                          )
                        "
                        :rules="[rules.atLeastOneClaimCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeClaimCommissionPSTTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_commission_pst'
                          )
                        "
                        :rules="[rules.atLeastOneClaimCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeClaimCommissionGSTTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_commission_gst'
                          )
                        "
                        :rules="[rules.atLeastOneClaimCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeClaimCommissionQSTTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_claim_commission_qst'
                          )
                        "
                        :rules="[rules.atLeastOneClaimCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col xs="12" sm="6" md="3" lg="3">
                      <v-checkbox
                        v-model="item.IncludeFlatCommission"
                        :label="
                          $t(
                            'components.plancreationwizard.include_flat_commission'
                          )
                        "
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="4" v-if="item.IncludeFlatCommission">
                      <v-checkbox
                        v-model="includeFlatCommissionTaxes"
                        :label="
                          $t(
                            'components.plancreationwizard.include_flat_commission_taxes'
                          )
                        "
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                  <v-row v-if="includeFlatCommissionTaxes">
                    <v-card-text>
                      {{
                        $t("components.plancreationwizard.flat_commission_taxes")
                      }}
                    </v-card-text>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeFlatPPTCommissionTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_flat_commission_ppt'
                          )
                        "
                        :rules="[rules.atLeastOneFlatCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeFlatRSTCommissionTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_flat_commission_rst'
                          )
                        "
                        :rules="[rules.atLeastOneFlatCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeFlatHSTCommissionTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_flat_commission_hst'
                          )
                        "
                        :rules="[rules.atLeastOneFlatCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeFlatPSTCommissionTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_flat_commission_pst'
                          )
                        "
                        :rules="[rules.atLeastOneFlatCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeFlatGSTCommissionTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_flat_commission_gst'
                          )
                        "
                        :rules="[rules.atLeastOneFlatCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-checkbox
                        v-model="item.IncludeFlatQSTCommissionTax"
                        :label="
                          $t(
                            'components.plancreationwizard.include_flat_commission_qst'
                          )
                        "
                        :rules="[rules.atLeastOneFlatCommissionTax]"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </template>
              </template>
            </v-container>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="closeCoverage">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn :disabled="!valid" color="primary" @click="saveCoverageItem">
            {{ $t("globals.save") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <v-dialog v-model="dialogs.delete" scrollable persistent max-width="400">
      <material-card
        :title="$t('components.plancreationwizard.remove_coverage')"
        :color="$vuetify.theme.themes.light.error"
        icon="remove"
      >
        <v-card-text>
          {{ $t("globals.are_you_sure") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color text @click="dialogs.delete = false">
            {{ $t("globals.cancel") }}
          </v-btn>
          <v-btn color="error" text @click="removeCoverageItem">
            {{ $t("globals.remove") }}
          </v-btn>
        </v-card-actions>
      </material-card>
    </v-dialog>
    <pbas-data-table
      ref="table"
      :dataProvider="provider"
      :rowActions="rowActions"
      :floatActions="floatActions"
      fileNamePrefix="coverage"
      identifier="coverageTable"
      @error="error = $event"
    ></pbas-data-table>
    <error-handler :error="error"></error-handler>
  </material-card>
</template>

<script>
import { mapGetters } from "vuex";
import { InMemoryProvider } from "pbas-vue-components";
import InMemoryCoverageHeaders from "@/headers/InMemoryCoverage";
import ErrorHandler from "@/components/ErrorHandler";
import { InMemoryHeader, InMemoryString } from "pbas-vue-components";
import { translate2 } from "@/trans2";
import TransTextbox from "@/components/TransTextbox";
import NoAutocompleteTextfield from "@/components/NoAutocompleteTextfield";
import AutoSelect from "@/components/AutoSelect";
import TransTextarea from "@/components/TransTextarea";
import TransSelect2 from "@/components/TransSelect2";
import CurrencyTextfield from "@/components/CurrencyTextfield.vue";
import TransEnum from "@/components/TransEnum.vue";
import TransMultiSelect2 from "@/components/TransMultiSelect2";
export default {
  components: {
    ErrorHandler,
    TransTextbox,
    NoAutocompleteTextfield,
    AutoSelect,
    TransTextarea,
    TransSelect2,
    CurrencyTextfield,
    TransEnum,
    TransMultiSelect2
  },
  props: [
    "coverage",
    "hasPremiums",
    "hasRemittances",
    "effectiveDate",
    "hasBillingImport",
    "hasAdminFees",
    "adminFeesPerTerm",
    "hasOperatingFees",
    "operatingFeesPerTerm",
    "trustFund",
    "planInsuranceCarriers",
    "disabled",
    "brokers",
  ],
  data() {
    return {
      includeTaxes: false,
      includeAdminTaxes: false,
      includeClaimAdminTaxes: false,
      includeClaimCommissionTaxes: false,
      includeFlatCommissionTaxes: false,
      includeCommissionTaxes: false,
      includeOperatingTaxes: false,
      fundingMethod: null,
      error: null,
      valid: false,
      dialogs: {
        edit: false,
        delete: false,
      },
      item: null,
      isNew: false,
      floatActions: [
        {
          name: "addCoverage",
          icon: "add",
          color: this.$vuetify.theme.themes.light.success,
          text: "globals.add_new",
          handler: () => {
            this.includeTaxes = false;
            this.includeAdminTaxes = false;
            this.includeClaimAdminTaxes = false;
            this.includeClaimCommissionTaxes = false;
            this.includeFlatCommissionTaxes = false;
            this.includeCommissionTaxes = false;
            this.includeOperatingTaxes = false;
            this.item = {
              Identifier: Math.random().toString(),
              IncludeAdminGST: false,
              IncludeAdminHST: false,
              IncludeAdminPPT: false,
              IncludeAdminPST: false,
              IncludeAdminQST: false,
              IncludeAdminRST: false,
              IncludeCommissionGST: false,
              IncludeCommissionHST: false,
              IncludeCommissionPPT: false,
              IncludeCommissionPST: false,
              IncludeCommissionQST: false,
              IncludeCommissionRST: false,
              IncludeGST: false,
              IncludeHST: false,
              IncludeOperatingGST: false,
              IncludeOperatingHST: false,
              IncludeOperatingPPT: false,
              IncludeOperatingPST: false,
              IncludeOperatingQST: false,
              IncludeOperatingRST: false,
              IncludePPT: false,
              IncludePST: false,
              IncludeQST: false,
              IncludeClaimPPTAdminTax: false,
              IncludeClaimRSTAdminTax: false,
              IncludeClaimHSTAdminTax: false,
              IncludeClaimPSTAdminTax: false,
              IncludeClaimGSTAdminTax: false,
              IncludeClaimQSTAdminTax: false,
              IncludeClaimCommissionPPTTax: false,
              IncludeClaimCommissionRSTTax: false,
              IncludeClaimCommissionHSTTax: false,
              IncludeClaimCommissionPSTTax: false,
              IncludeClaimCommissionGSTTax: false,
              IncludeClaimCommissionQSTTax: false,
              IncludeFlatPPTCommissionTax: false,
              IncludeFlatRSTCommissionTax: false,
              IncludeFlatHSTCommissionTax: false,
              IncludeFlatPSTCommissionTax: false,
              IncludeFlatGSTCommissionTax: false,
              IncludeFlatQSTCommissionTax: false,
            };
            this.isNew = true;
            this.dialogs.edit = true;
          },
          visible: () => !this.disabled,
        },
      ],
      rowActions: [
        {
          name: "editCoverage",
          icon: "edit",
          title: "globals.edit",
          handler: (item) => {
            console.log(JSON.stringify(item));
            this.item = JSON.parse(JSON.stringify(item));
            this.includeTaxes =
              this.item.IncludePPT ||
              this.item.IncludeRST ||
              this.item.IncludeHST ||
              this.item.IncludePST ||
              this.item.IncludeGST;
            this.includeAdminTaxes =
              this.item.IncludeAdminPPT ||
              this.item.IncludeAdminRST ||
              this.item.IncludeAdminHST ||
              this.item.IncludeAdminPST ||
              this.item.IncludeAdminGST;
            this.includeClaimAdminTaxes =
              this.item.IncludeClaimPPTAdminTax ||
              this.item.IncludeClaimRSTAdminTax ||
              this.item.IncludeClaimHSTAdminTax ||
              this.item.IncludeClaimPSTAdminTax ||
              this.item.IncludeClaimGSTAdminTax ||
              this.item.IncludeClaimQSTAdminTax;
            this.includeClaimCommissionTaxes =
              this.item.IncludeClaimCommissionPPTTax ||
              this.item.IncludeClaimCommissionRSTTax ||
              this.item.IncludeClaimCommissionHSTTax ||
              this.item.IncludeClaimCommissionPSTTax ||
              this.item.IncludeClaimCommissionGSTTax ||
              this.item.IncludeClaimCommissionQSTTax;
            this.includeFlatCommissionTaxes =
              this.item.IncludeFlatPPTCommissionTax ||
              this.item.IncludeFlatRSTCommissionTax ||
              this.item.IncludeFlatHSTCommissionTax ||
              this.item.IncludeFlatPSTCommissionTax ||
              this.item.IncludeFlatGSTCommissionTax ||
              this.item.IncludeFlatQSTCommissionTax;
            this.includeCommissionTaxes =
              this.item.IncludeCommissionPPT ||
              this.item.IncludeCommissionRST ||
              this.item.IncludeCommissionHST ||
              this.item.IncludeCommissionPST ||
              this.item.IncludeCommissionGST;
            this.includeOperatingTaxes =
              this.item.IncludeOperatingPPT ||
              this.item.IncludeOperatingRST ||
              this.item.IncludeOperatingHST ||
              this.item.IncludeOperatingPST ||
              this.item.IncludeOperatingGST;

              if (this.item.IncludePPT===null || this.item.IncludePPT===undefined) {
                this.item.IncludePPT = false;
              }
              if (this.item.IncludeRST===null || this.item.IncludeRST===undefined) {
                this.item.IncludeRST = false;
              }
              if (this.item.IncludeHST===null || this.item.IncludeHST===undefined) {
                this.item.IncludeHST = false;
              }
              if (this.item.IncludePST===null || this.item.IncludePST===undefined) {
                this.item.IncludePST = false;
              }
              if (this.item.IncludeGST===null || this.item.IncludeGST===undefined) {
                this.item.IncludeGST = false;
              }
              if (this.item.IncludeAdminPPT===null || this.item.IncludeAdminPPT===undefined) {
                this.item.IncludeAdminPPT = false;
              }
              if (this.item.IncludeAdminRST===null || this.item.IncludeAdminRST===undefined) {
                this.item.IncludeAdminRST = false;
              }
              if (this.item.IncludeAdminHST===null || this.item.IncludeAdminHST===undefined) {
                this.item.IncludeAdminHST = false;
              }
              if (this.item.IncludeAdminPST===null || this.item.IncludeAdminPST===undefined) {
                this.item.IncludeAdminPST = false;
              }
              if (this.item.IncludeAdminGST===null || this.item.IncludeAdminGST===undefined) {
                this.item.IncludeAdminGST = false;
              }
              if (this.item.IncludeClaimPPTAdminTax===null || this.item.IncludeClaimPPTAdminTax===undefined) {
                this.item.IncludeClaimPPTAdminTax = false;
              }
              if (this.item.IncludeClaimRSTAdminTax===null || this.item.IncludeClaimRSTAdminTax===undefined) {
                this.item.IncludeClaimRSTAdminTax = false;
              }
              if (this.item.IncludeClaimHSTAdminTax===null || this.item.IncludeClaimHSTAdminTax===undefined) {
                this.item.IncludeClaimHSTAdminTax = false;
              }
              if (this.item.IncludeClaimPSTAdminTax===null || this.item.IncludeClaimPSTAdminTax===undefined) {
                this.item.IncludeClaimPSTAdminTax = false;
              }
              if (this.item.IncludeClaimGSTAdminTax===null || this.item.IncludeClaimGSTAdminTax===undefined) {
                this.item.IncludeClaimGSTAdminTax = false;
              }
              if (this.item.IncludeClaimQSTAdminTax===null || this.item.IncludeClaimQSTAdminTax===undefined) {
                this.item.IncludeClaimQSTAdminTax = false;
              }
              if (this.item.IncludeClaimCommissionPPTTax===null || this.item.IncludeClaimCommissionPPTTax===undefined) {
                this.item.IncludeClaimCommissionPPTTax = false;
              }
              if (this.item.IncludeClaimCommissionRSTTax===null || this.item.IncludeClaimCommissionRSTTax===undefined) {
                this.item.IncludeClaimCommissionRSTTax = false;
              }
              if (this.item.IncludeClaimCommissionHSTTax===null || this.item.IncludeClaimCommissionHSTTax===undefined) {
                this.item.IncludeClaimCommissionHSTTax = false;
              }
              if (this.item.IncludeClaimCommissionPSTTax===null || this.item.IncludeClaimCommissionPSTTax===undefined) {
                this.item.IncludeClaimCommissionPSTTax = false;
              }
              if (this.item.IncludeClaimCommissionGSTTax===null || this.item.IncludeClaimCommissionGSTTax===undefined) {
                this.item.IncludeClaimCommissionGSTTax = false;
              }
              if (this.item.IncludeClaimCommissionQSTTax===null || this.item.IncludeClaimCommissionQSTTax===undefined) {
                this.item.IncludeClaimCommissionQSTTax = false;
              }
              if (this.item.IncludeFlatPPTCommissionTax===null || this.item.IncludeFlatPPTCommissionTax===undefined) {
                this.item.IncludeFlatPPTCommissionTax = false;
              }
              if (this.item.IncludeFlatRSTCommissionTax===null || this.item.IncludeFlatRSTCommissionTax===undefined) {
                this.item.IncludeFlatRSTCommissionTax = false;
              }
              if (this.item.IncludeFlatHSTCommissionTax===null || this.item.IncludeFlatHSTCommissionTax===undefined) {
                this.item.IncludeFlatHSTCommissionTax = false;
              }
              if (this.item.IncludeFlatPSTCommissionTax===null || this.item.IncludeFlatPSTCommissionTax===undefined) {
                this.item.IncludeFlatPSTCommissionTax = false;
              }
              if (this.item.IncludeFlatGSTCommissionTax===null || this.item.IncludeFlatGSTCommissionTax===undefined) {
                this.item.IncludeFlatGSTCommissionTax = false;
              }
              if (this.item.IncludeFlatQSTCommissionTax===null || this.item.IncludeFlatQSTCommissionTax===undefined) {
                this.item.IncludeFlatQSTCommissionTax = false;
              }
              if (this.item.IncludeCommissionPPT===null || this.item.IncludeCommissionPPT===undefined) {
                this.item.IncludeCommissionPPT = false;
              }
              if (this.item.IncludeCommissionRST===null || this.item.IncludeCommissionRST===undefined) {
                this.item.IncludeCommissionRST = false;
              }
              if (this.item.IncludeCommissionHST===null || this.item.IncludeCommissionHST===undefined) {
                this.item.IncludeCommissionHST = false;
              }
              if (this.item.IncludeCommissionPST===null || this.item.IncludeCommissionPST===undefined) {
                this.item.IncludeCommissionPST = false;
              }
              if (this.item.IncludeCommissionGST===null || this.item.IncludeCommissionGST===undefined) {
                this.item.IncludeCommissionGST = false;
              }
              if (this.item.IncludeOperatingPPT===null || this.item.IncludeOperatingPPT===undefined) {
                this.item.IncludeOperatingPPT = false;
              }
              if (this.item.IncludeOperatingRST===null || this.item.IncludeOperatingRST===undefined) {
                this.item.IncludeOperatingRST = false;
              }
              if (this.item.IncludeOperatingHST===null || this.item.IncludeOperatingHST===undefined) {
                this.item.IncludeOperatingHST = false;
              }
              if (this.item.IncludeOperatingPST===null || this.item.IncludeOperatingPST===undefined) {
                this.item.IncludeOperatingPST = false;
              }
              if (this.item.IncludeOperatingGST===null || this.item.IncludeOperatingGST===undefined) {
                this.item.IncludeOperatingGST = false;
              }

            this.isNew = false;
            if (
              this.item.PlanInsuranceCarrier !== null &&
              this.item.PlanInsuranceCarrier !== undefined
            ) {
              this.fundingMethod = "Insurance Carrier";
            } else if (
              this.item.TrustFund !== null &&
              this.item.TrustFund !== undefined
            ) {
              this.fundingMethod = "Trust Fund";
            }
            this.dialogs.edit = true;
          },
          visible: () => !this.disabled,
        },
        {
          name: "removeCoverage",
          icon: "delete",
          title: "globals.remove",
          handler: (item) => {
            this.item = JSON.parse(JSON.stringify(item));
            this.dialogs.delete = true;
          },
          visible: () => !this.disabled,
        },
      ],
      provider: undefined,
      headers: InMemoryCoverageHeaders,
      rules: {
        decimal: (v) => {
          if (v === null || v === undefined || v === 0 || v === "") return true;
          return !isNaN(parseFloat(v)) || this.$t("validation.must_be_decimal");
        },
        atLeastOneTax: (v) => {
          if (this.item === null || this.item === undefined) return false;
          let res =
            this.item.IncludePPT ||
            this.item.IncludeRST ||
            this.item.IncludeHST ||
            this.item.IncludeGST ||
            this.item.IncludePST ||
            this.$t("validation.at_least_one_tax");
          return res;
        },
        atLeastOneAdminTax: (v) => {
          if (this.item === null || this.item === undefined) return false;
          let res =
            this.item.IncludeAdminPPT ||
            this.item.IncludeAdminRST ||
            this.item.IncludeAdminHST ||
            this.item.IncludeAdminGST ||
            this.item.IncludeAdminPST ||
            this.$t("validation.at_least_one_admin_tax");
          return res;
        },
        atLeastOneClaimAdminFeeTax: (v) => {
          if (this.item === null || this.item === undefined) return false;
          let res =
            this.item.IncludeClaimPPTAdminTax ||
            this.item.IncludeClaimRSTAdminTax ||
            this.item.IncludeClaimHSTAdminTax ||
            this.item.IncludeClaimPSTAdminTax ||
            this.item.IncludeClaimGSTAdminTax ||
            this.item.IncludeClaimQSTAdminTax ||
            this.$t("validation.at_least_one_claim_admin_fee_tax");
          console.log(res);
          return res;
        },
        atLeastOneClaimCommissionTax: (v) => {
          if (this.item === null || this.item === undefined) return false;
          let res =
            this.item.IncludeClaimCommissionPPTTax ||
            this.item.IncludeClaimCommissionRSTTax ||
            this.item.IncludeClaimCommissionHSTTax ||
            this.item.IncludeClaimCommissionPSTTax ||
            this.item.IncludeClaimCommissionGSTTax ||
            this.item.IncludeClaimCommissionQSTTax ||
            this.$t("validation.at_least_one_claim_commission_tax");
          return res;
        },
        atLeastOneFlatCommissionTax: (v) => {
          if (this.item === null || this.item === undefined) return false;
          let res =
            this.item.IncludeFlatPPTCommissionTax ||
            this.item.IncludeFlatRSTCommissionTax ||
            this.item.IncludeFlatHSTCommissionTax ||
            this.item.IncludeFlatPSTCommissionTax ||
            this.item.IncludeFlatGSTCommissionTax ||
            this.item.IncludeFlatQSTCommissionTax ||
            this.$t("validation.at_least_one_flat_commission_tax");
          console.log(res);
          return res;
        },
        atLeastOneCommissionTax: (v) => {
          if (this.item === null || this.item === undefined) return false;
          let res =
            this.item.IncludeCommissionPPT ||
            this.item.IncludeCommissionRST ||
            this.item.IncludeCommissionHST ||
            this.item.IncludeCommissionGST ||
            this.item.IncludeCommissionPST ||
            this.$t("validation.at_least_one_commission_tax");
          return res;
        },
        atLeastOneOperatingTax: (v) => {
          if (this.item === null || this.item === undefined) return false;
          let res =
            this.item.IncludeOperatingPPT ||
            this.item.IncludeOperatingRST ||
            this.item.IncludeOperatingHST ||
            this.item.IncludeOperatingGST ||
            this.item.IncludeOperatingPST ||
            this.$t("validation.at_least_one_operating_tax");
          return res;
        },
        minmax: () => {
          if (this.item === null || this.item === undefined) return true;
          if (
            this.item.MinVolume === null ||
            this.item.MinVolume === undefined ||
            this.item.MinVolume === ""
          )
            return true;
          if (
            this.item.MaxVolume === null ||
            this.item.MaxVolume === undefined ||
            this.item.MaxVolume === ""
          )
            return true;
          return (
            parseFloat(this.item.MinVolume) < parseFloat(this.item.MaxVolume) ||
            this.$t("validation.minmax")
          );
        },
      },
    };
  },
  computed: {
    ...mapGetters("common", ["language"]),
    ...mapGetters("plansMembers", ["options"]),
    coverageFormTitle() {
      return this.isNew
        ? "components.plancreationwizard.create_new_coverage"
        : "components.memberview.view_edit_coverage_title";
    },
    coverageFormIcon() {
      return this.isNew ? "add" : "edit";
    },
    formColor() {
      return this.isNew ? this.$vuetify.theme.themes.light.success : this.$vuetify.theme.themes.light.primary;
    },
    fundingMethods() {
      var fundingMethods = [];
      if (this.hasRemittances) {
        fundingMethods.push("Trust Fund");
      }
      if (this.hasPremiums) {
        fundingMethods.push("Insurance Carrier");
      }
      return fundingMethods;
    },
  },
  watch: {
    coverage: {
      async handler(v) {
        if (this.provider !== null && this.provider !== undefined) {
          this.provider.items = v;
          await this.$refs.table.getDataAsync();
        }
      },
      immediate: true,
      deep: true,
    },
    "item.MinVolume": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.MaxVolume": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludePPT": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeRST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeHST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeGST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludePST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeAdminPPT": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeAdminRST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeAdminHST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeAdminGST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeAdminPST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeClaimPPTAdminTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeClaimRSTAdminTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeClaimHSTAdminTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeClaimPSTAdminTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeClaimGSTAdminTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeClaimQSTAdminTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeClaimCommissionPPTTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeClaimCommissionRSTTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeClaimCommissionHSTTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeClaimCommissionPSTTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeClaimCommissionGSTTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeClaimCommissionQSTTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeFlatPPTCommissionTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeFlatRSTCommissionTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeFlatHSTCommissionTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeFlatPSTCommissionTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeFlatGSTCommissionTax": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeFlatQSTCommissionTax": {
      handler(v) {
        console.log("########################################")
        if (this.$refs.form) {
          console.log("$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$$")
          this.$refs.form.validate();
        }
      },
      immediate: true,
    },
    "item.IncludeCommissionPPT": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeCommissionRST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeCommissionHST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeCommissionGST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeCommissionPST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeOperatingPPT": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeOperatingRST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeOperatingHST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeOperatingGST": {
      handler(v) {
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    "item.IncludeOperatingPST": {
      handler(v) {
        console.log("##################33333333333333333######################")
        if (this.$refs.form) {
          console.log("$$$$$$$$$$$$$$$$433333333333333333333$$$$$$$$$$$$$$$$$$")
          this.$refs.form.validate();
        }
      },
      immediate: true,
    },
    hasBillingImport: {
      handler(v) {
        if (!v) {
          if (this.provider !== null && this.provider !== undefined) {
            for (let c of this.provider.items) {
              c.IncludedInEmployerContribution = false;
            }
          }
          this.$emit("update-coverage", this.provider.items);
        }
      },
    },
    "item.IsFreeOfCharge": {
      handler(v, old) {
        if (old !== null && old !== undefined && v !== old) {
          this.item.IsMandatory = false;
          this.item.HasAdminFees = false;
          this.item.HasOperatingFees = false;
          this.item.HasVolume = false;
          this.item.HasVolumeChoice = false;
          this.item.HasCoverageChoice = false;
          this.item.HasCOB = false;
          this.item.IsTaxableBenefit = false;
          this.includeTaxes = false;
        }
      },
    },
    "item.IsMandatory": {
      handler(v) {
        if (v) {
          this.item.IsOptional = false;
          this.item.IsWaivable = false;
        }
      },
    },
    hasAdminFees: {
      handler(v) {
        if (!v) {
          if (this.provider !== null && this.provider !== undefined) {
            for (let c of this.provider.items) {
              c.HasAdminFees = false;
              c.includeAdminTaxes = false;
            }
          }
          this.$emit("update-coverage", this.provider.items);
        }
      },
    },
    "item.Broker": {
      handler(v) {
        if (!v) {
          this.item.HasCommissions = false;
          this.item.IncludeClaimCommission = false;
          this.item.IncludeFlatCommission = false;
        }
      },
    },
    "item.IncludeClaimCommission" :{
      handler(v) {
        if (!v) {
          this.includeClaimCommissionTaxes = false;
        }
      }
    },
    "item.IncludeFlatCommission" :{
      handler(v) {
        if (!v) {
          this.includeFlatCommissionTaxes = false;
        }
      }
    },
    "item.HasCommissions" :{
      handler(v) {
        if (!v) {
          this.includeCommissionTaxes = false;
        }
      }
    },
    // adminFeesPerTerm: {
    //   handler(v) {
    //     if (v) {
    //       if (this.provider !== null && this.provider !== undefined) {
    //         for (let c of this.provider.items) {
    //           console.log(c);
    //           c.HasAdminFees = false;
    //           c.includeAdminTaxes = false;
    //         }
    //       }
    //       this.$emit("update-coverage", this.provider.items);
    //     }
    //   },
    // },
    hasOperatingFees: {
      handler(v) {
        if (!v) {
          if (this.provider !== null && this.provider !== undefined) {
            for (let c of this.provider.items) {
              c.HasOperatingFees = false;
              c.includeOperatingTaxes = false;
            }
          }
          this.$emit("update-coverage", this.provider.items);
        }
      },
    },
    operatingFeesPerTerm: {
      handler(v) {
        if (v) {
          if (this.provider !== null && this.provider !== undefined) {
            for (let c of this.provider.items) {
              c.HasOperatingFees = false;
              c.includeOperatingTaxes = false;
            }
          }
          this.$emit("update-coverage", this.provider.items);
        }
      },
    },
    "item.HasVolume": {
      handler(v) {
        if (!v && this.item) {
          this.item.HasNEL = false;
          delete this.item.NEL;
        }
      },
    },
    "item.HasNEL": {
      handler(v) {
        if (!v && this.item) {
          delete this.item.NEL;
        }
      },
    },
    "item.HasVolumeChoice": {
      handler(v) {
        if (!v && this.item) {
          delete this.item.MinVolume;
          delete this.item.MaxVolume;
        }
      },
    },
    "item.HasCoverageChoice": {
      handler(v) {
        if (!v && this.item) {
          delete this.item.TracksDependantsEnum;
          this.item.RestrictToDefaultCoverageStatus = false;
        }
      },
    },
    includeTaxes: {
      handler(v) {
        if (!v) {
          if (this.item) {
            this.item.IncludePPT = false;
            this.item.IncludeHST = false;
            this.item.IncludeRST = false;
            this.item.IncludeGST = false;
            this.item.IncludePST = false;
          }
        }
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    includeAdminTaxes: {
      handler(v) {
        if (!v) {
          if (this.item) {
            this.item.IncludeAdminPPT = false;
            this.item.IncludeAdminHST = false;
            this.item.IncludeAdminRST = false;
            this.item.IncludeAdminGST = false;
            this.item.IncludeAdminPST = false;
          }
        }
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    includeClaimAdminTaxes: {
      handler(v) {
        if (!v && this.item) {
          this.item.IncludeClaimPPTAdminTax = false;
          this.item.IncludeClaimRSTAdminTax = false;
          this.item.IncludeClaimHSTAdminTax = false;
          this.item.IncludeClaimPSTAdminTax = false;
          this.item.IncludeClaimGSTAdminTax = false;
          this.item.IncludeClaimQSTAdminTax = false;
        }
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    includeClaimCommissionTaxes: {
      handler(v) {
        if (!v && this.item) {
          this.item.IncludeClaimCommissionPPTTax = false;
          this.item.IncludeClaimCommissionRSTTax = false;
          this.item.IncludeClaimCommissionHSTTax = false;
          this.item.IncludeClaimCommissionPSTTax = false;
          this.item.IncludeClaimCommissionGSTTax = false;
          this.item.IncludeClaimCommissionQSTTax = false;
        }
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    includeFlatCommissionTaxes: {
      handler(v) {
        if (!v && this.item) {
          this.item.IncludeFlatPPTCommissionTax = false;
          this.item.IncludeFlatRSTCommissionTax = false;
          this.item.IncludeFlatHSTCommissionTax = false;
          this.item.IncludeFlatPSTCommissionTax = false;
          this.item.IncludeFlatGSTCommissionTax = false;
          this.item.IncludeFlatQSTCommissionTax = false;
        }
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    includeCommissionTaxes: {
      handler(v) {
        if (!v) {
          if (this.item) {
            this.item.IncludeCommissionPPT = false;
            this.item.IncludeCommissionHST = false;
            this.item.IncludeCommissionRST = false;
            this.item.IncludeCommissionGST = false;
            this.item.IncludeCommissionPST = false;
          }
        }
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    includeOperatingTaxes: {
      handler(v) {
        if (!v) {
          if (this.item) {
            this.item.IncludeOperatingPPT = false;
            this.item.IncludeOperatingHST = false;
            this.item.IncludeOperatingRST = false;
            this.item.IncludeOperatingGST = false;
            this.item.IncludeOperatingPST = false;
          }
        }
        if (this.$refs.form) this.$refs.form.validate();
      },
      immediate: true,
    },
    hasPremiums: {
      handler() {
        this.createHeaders();
        if (this.coverage !== null && this.coverage !== undefined) {
          if (this.provider === null || this.provider === undefined) {
            this.provider = new InMemoryProvider(this.coverage, this.headers);
          } else {
            this.provider.items = this.coverage;
            this.$refs.table.getDataAsync();
          }
        }
      },
    },
    hasRemittances: {
      handler() {
        this.createHeaders();
        if (this.coverage !== null && this.coverage !== undefined) {
          if (this.provider === null || this.provider === undefined) {
            this.provider = new InMemoryProvider(this.coverage, this.headers);
          } else {
            this.provider.items = this.coverage;
            this.$refs.table.getDataAsync();
          }
        }
      },
    },
    trustFund: {
      handler(v) {
        if (v === null || v === undefined) {
          if (this.provider !== null && this.provider !== undefined) {
            for (let c of this.provider.items) {
              delete c.TrustFund;
            }
          }
          this.$emit("update-coverage", this.provider.items);
        }
      },
    },
    planInsuranceCarriers: {
      handler(v) {
        if (v === null || v === undefined) {
          if (this.provider !== null && this.provider !== undefined) {
            for (let c of this.provider.items) {
              delete c.PlanInsuranceCarrier;
            }
            this.$emit("update-coverage", this.provider.items);
          }
        } else {
          if (this.provider !== null && this.provider !== undefined) {
            for (let c of this.provider.items) {
              if (
                v.filter(
                  (o) => c.PlanInsuranceCarrier !==null && c.PlanInsuranceCarrier!== undefined && o.Identifier === c.PlanInsuranceCarrier.Identifier
                ).length === 0
              ) {
                delete c.PlanInsuranceCarrier; 
              }
            }
            this.$emit("update-coverage", this.provider.items);
          }
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.createHeaders();
    if (this.coverage !== null && this.coverage !== undefined) {
      this.provider = new InMemoryProvider(this.coverage, this.headers);
    }
  },
  methods: {
    createHeaders() {
      this.headers = [...InMemoryCoverageHeaders];
      if (this.hasPremiums) {
        this.headers.splice(
          this.headers.length - 2,
          0,
          new InMemoryHeader({
            title: "globals.insurance_carrier",
            type: new InMemoryString(
              "PlanInsuranceCarrier/InsuranceCarrier/Translations[]/Name",
              {
                getString: (item) => {
                  if (
                    item.PlanInsuranceCarrier === null ||
                    item.PlanInsuranceCarrier === undefined
                  )
                    return "";
                  const txt = translate2(
                    item.PlanInsuranceCarrier.InsuranceCarrier,
                    "Name"
                  );
                  if (item.PlanInsuranceCarrier.Policy) {
                    return `${txt} (${item.PlanInsuranceCarrier.Policy.Number})`;
                  }
                  return txt;
                },
              }
            ),
            sortable: true,
            visible: true,
            searchable: true,
            orderByKey: "PlanInsuranceCarrier/InsuranceCarrier/Name",
          })
        );
      }
      if (this.hasRemittances) {
        this.headers.splice(
          this.headers.length - 2,
          0,
          new InMemoryHeader({
            title: "components.getreports.trust_fund",
            type: new InMemoryString("TrustFund/Translations[]/Name", {
              getString: (item) =>
                item.TrustFund ? translate2(item.TrustFund, "Name") : "",
            }),
            sortable: true,
            visible: true,
            searchable: true,
            orderByKey: "TrustFund/Name",
          })
        );
      }
    },
    saveCoverageItem() {
      console.log(JSON.stringify(this.item));
      let item = JSON.parse(JSON.stringify(this.item));
      item.EffectiveDate = this.effectiveDate;
      if (this.fundingMethod === "Insurance Carrier") {
        item.TrustFundId = null;
        delete item.TrustFund;
      } else if (this.fundingMethod === "Trust Fund") {
        item.PlanInsuranceCarrierId = null;
        delete item.PlanInsuranceCarrier;
      }

      let min = parseFloat(item.MinVolume);
      if (isNaN(min)) {
        delete item.MinVolume;
      }

      let max = parseFloat(item.MaxVolume);
      if (isNaN(max)) {
        delete item.MaxVolume;
      }

      if (item.IsFreeOfCharge) {
        item.IsMandatory = true;
      }

      if (!item.HasNEL) {
        delete item.NEL;
      }

      if (this.isNew) {
        this.addCoverageItem(item);
      } else {
        this.updateCoverageItem(item);
      }
    },
    updateCoverageItem(item) {
      console.log(JSON.stringify(item));
      let existing;
      existing = this.provider.items.filter(
        (o) => o.Identifier === item.Identifier
      )[0];

      item.Name = item.Translations.filter(
        (o) => o.LanguageCode === this.language.language
      )[0].Name;

      this.provider.items[this.provider.items.indexOf(existing)] = item;

      this.closeCoverage();
      this.$refs.table.getDataAsync();
      this.$emit("update-coverage", this.provider.items);
    },
    addCoverageItem(item) {
      item.Name = this.item.Translations.filter(
        (o) => o.LanguageCode === this.language.language
      )[0].Name;
      this.provider.items.push(item);
      this.closeCoverage();
      this.$refs.table.getDataAsync();
      this.$emit("update-coverage", this.provider.items);
    },
    removeCoverageItem() {
      let item = JSON.parse(JSON.stringify(this.item));
      let existing;
      existing = this.provider.items.filter(
        (o) => o.Identifier === item.Identifier
      )[0];

      this.provider.items.splice(this.provider.items.indexOf(existing), 1);
      this.closeCoverage();
      this.$refs.table.getDataAsync();
      this.$emit("update-coverage", this.provider.items);
    },
    closeCoverage() {
      this.fundingMethod = null;
      this.dialogs.edit = false;
      this.dialogs.delete = false;
      this.item = null;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },
  },
};
</script>

<style></style>
